import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import {useReviewRequestsQueryKey} from '../components/overview/data/route-queries'
import {useMergeBoxQueryKey} from '@github-ui/mergebox/use-merge-box-query-key'
import type {CopilotReviewEffortRequest} from '@github-ui/pull-request-page-data-tooling/copilot-review-effort'

type ReviewerIdentity = {reviewerId: number; reviewerLogin?: never} | {reviewerId?: never; reviewerLogin: string}

export type MutationData = ReviewerIdentity & {
  copilotReviewEffortRequest?: CopilotReviewEffortRequest
}

type Callbacks = {
  onError?: (error: Error) => void
  onSuccess?: () => void
}

export function useReRequestReviewMutation({onError, onSuccess}: Callbacks = {}) {
  const reviewRequestsQueryKey = useReviewRequestsQueryKey()
  const mergeBoxQueryKey = useMergeBoxQueryKey()
  const apiURL = usePageDataUrl(PageData.reRequestReviewFromUser)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {
          ...(data.reviewerId ? {reviewerId: data.reviewerId} : {}),
          ...(data.reviewerLogin ? {reviewerLogin: data.reviewerLogin} : {}),
          ...data.copilotReviewEffortRequest?.fields,
        },
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      try {
        data.copilotReviewEffortRequest?.acceptResponse(json)
      } catch (error) {
        reportError(error)
      }
      return json
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: reviewRequestsQueryKey}, {cancelRefetch: false}),
        queryClient.invalidateQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false}),
      ])
      onSuccess?.()
    },
    onError: (e: Error) => {
      onError?.(e)
    },
  })
}
